import { HashLink as Link } from 'react-router-hash-link';
import routes from 'src/routes';
import url from 'src/util/url';

export const SampleSteps = {
    Setup: 0,
    CreatePublicFolder1: 1,
    PublicHtmlPage1: 2,
    PublicHtmlPage2: 3,
    PublicJavascript: 4,
    PublicCSS: 5,
    CreateModel: 6,    
    CreateController: 7,
    AddRESTfulReference: 8,
    UserNamePassword: 9,
    BoilerPlate: 10,
    Build: 11,
    Run: 12,
    Demo: 13
}

export const SampleStepsDoco =
    [
        {
            step: "Step 1 - Setting up your project",
            description: 
            <div>
                <p>
                    Create a parent folder where both your iFrame project and the Client RESTful libraries will go under.  
                    For example, you may wish to create a folder on <code>C:\</code> drive called <code>QuickStart</code>, such as <code>C:\QuickStart</code>.  In the QuickStart guide,
                    <code>C:\QuickStart</code> will be used as the parent folder.
                </p>
                <p>
                    The best approach is to generate the Client RESTful libraries that will connect you to BPoint.  To follow along, 
                    refer to the steps in <Link to={routes.getstarted.openapigenerator}>Downloading OpenAPI Generator and Generating BPoint Client RESTful Library</Link>.
                    Make sure the library is generated under the parent folder such as <code>C:\QuickStart</code>.
                </p>
                <p>
                    Finally, run the command below to create your <code>BPoint.QuickStart</code> project.  Please run the command under your parent folder (such as <code>C:\QuickStart</code>).
                </p>
            </div>,
            DocoUrl: null,
            language: 'CMD',
            code: `dotnet new web --name BPoint.QuickStart`,
            copyButton: true
        },

        {
            step: "Step 2 - Replace Program.cs code with start-up code",
            description: <p>Copy and paste boilerplate code into <code>Program.cs</code>, replacing any existing code.</p>,
            DocoUrl: null,
            language: 'C#',
            code: `
using Microsoft.AspNetCore;

namespace Bpoint.QuickStart
{
    public class Program
    {
        public static void Main(string[] args)
        {
            WebHost.CreateDefaultBuilder(args)
                .UseUrls("http://127.0.0.1:5333")
                .UseWebRoot("public")
                .UseStartup<Startup>()
                .Build()
                .Run();
        }
    }

    public class Startup
    {
        public void ConfigureServices(IServiceCollection services)
        {
            services.AddControllers();
        }

        public void Configure(IApplicationBuilder app, IWebHostEnvironment env)
        {
            if (env.IsDevelopment())
            {
                app.UseDeveloperExceptionPage();
            }

            app.UseRouting();
            app.UseStaticFiles();
            app.UseEndpoints(endpoints => endpoints.MapControllers());
        }
    }
}            
`,
            copyButton: true
        },

        {
            step: "Step 3 - Create Model class",
            description: <p>Add a model class <code>ProcessPaymentPayload</code> to the project (for example, saved to: <code>C:\QuickStart\BPoint.QuickStart\Model\ProcessPaymentPayload.cs</code>). 
            The model holds the AuthKey used to identify the payment to process.</p>,
            DocoUrl: null,
            language: 'C#',
            code: `
using System.Text.Json.Serialization;

namespace Bpoint.QuickStart.Model
{   
    public class ProcessPaymentPayload
    {
        [JsonPropertyName("authkey")] 
        public string AuthKey { get; set; }
    }
}                 
`,
            copyButton: true
        },



        {
            step: "Step 4 - Create public folder",
            description: <div><p>Create a public folder for front-end HTML, JavaScript and CSS implementation.</p>
            <p>Change your directory to <code>BPoint.QuickStart</code>.</p>
            <p>Create folder <code>C:\QuickStart\BPoint.QuickStart\public</code> for front-end HTML, JavaScript and CSS implementation.</p></div>,
            DocoUrl: null,
            language: 'CMD',
            code: `cd BPoint.QuickStart`,
            copyButton: true
        },

        {
            step: "Step 5 - Create HTML page",
            description: <p>The front end is a HTML page that accepts credit card payments.  Create a html file called <code>payment.html</code> under the <code>public</code> folder and add the following code.
                Feel free to design your own payment page, the code below is only intended for demonstration purposes.</p>,
            DocoUrl: null,
            language: 'HTML',
            code: `
<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="utf-8" />
    <title>Accept a payment</title>
    <meta name="description" content="A demo of a payment on Bpoint using I-Frame" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <link rel="stylesheet" href="payment.css" />
    <script src="payment.js" defer></script>
    
</head>
<body>
    <div id="paymentMethodForm" style="display: none;">
        <div class="fieldGroup">
            <label>Card number:</label>
            <div class="frame-field" id="cardNumberField"></div>
        </div>

        <div class="fieldGroup">
            <label>Expiry month:</label>
            <div class="frame-field" id="expiryMonthField"></div>
        </div>

        <div class="fieldGroup">
            <lable>Expiry year:</lable>
            <div class="frame-field" id="expiryYearField"></div>
        </div>

        <div class="fieldGroup">
            <label>CVN:</label>
            <div class="frame-field" id="cvnField"></div>
        </div>

        <div class="fieldGroup">
            <label>Name on card:</label>
            <div class="frame-field" id="nameOnCardField"></div>
        </div>

        <div class="fieldGroup">
            <button id="submitButton">Submit</button>
        </div>
    </div>

    <div id="result" style="display: none">
        <p id="outcome" />
    </div>

    <script src="${url.api("clientscripts/api.js")}" type="text/javascript"></script>
</body>
</html>
            `,
            copyButton: true
        },

        {
            step: null,
            description: <p>Take note that in your implementation, you must include the following script tag
                 in your page as can be seen in the code above to enable JavaScript integration.</p>,
            DocoUrl: null,
            language: 'html',
            code: `<script src="${url.api("clientscripts/api.js")}" type="text/javascript"></script>`            
        },

        {
            step: "Step 6 - Create JavaScript",
            description: <p>The JavaScript implementation has two functions:
                
                <ul>
                    <li>The first is <code>initiatePayment()</code> which obtains an AuthKey representing the payment session.</li>
                    <li>The second is <code>processPayment()</code> which will finalise and process the payment once all details are collected.</li>
                </ul>
                <p>The <code>initiatePayment()</code> function contains a call to <b>BPOINT.txn.authkey.setupIframeFields()</b> to attach the iframe fields to the AuthKey and set the style of the fields.
                The first parameter sets the AuthKey, and the second parameter defines which fields to display and the three field styles, <b>focused, valid and invalid</b>.  For more details check out: &nbsp;
                <Link to={routes.api.txn.authkey.iframeFields}>Iframe Fields</Link>.</p>
                <p>Under the <code>public</code> folder add file <code>payment.js</code> and copy the code.</p>
                </p>,
            DocoUrl: null,
            language: 'Javascript',
            code: `
let authkey = "";
initiatePayment();            

async function initiatePayment() {
    
    const response = await fetch("/checkout/initiate-payment", {
        method: "GET",
        headers: { "Content-Type": "text/plain" }
    });

    authkey = await response.text();

    BPOINT.txn.authkey.setupIframeFields(
        authkey,
        {
            card: {
                number: {
                    selector: "#cardNumberField",
                },
                expiry: {
                    month: {
                        selector: "#expiryMonthField",
                    },
                    year: {
                        selector: "#expiryYearField",
                    },
                },
                cvn: {
                    selector: "#cvnField",
                },
                name: {
                    selector: "#nameOnCardField",
                },
            },
            fieldClasses: {
                focused: 'is-focused',
                invalid: 'is-invalid',
                valid: 'is-valid',
            },
            onFormLoaded: function (controller) {                
                document.querySelector("#paymentMethodForm").style.display = "block";
                document.querySelector("#submitButton").addEventListener("click", function () {
                    controller.submit(function (code, data) {
                        if (code === "success") {
                            processPayment();
                        } else {
                            alert(\`\${data.message} - \${data?.details[0]?.code} - \${data?.details[0]?.field} - \${data?.details[0]?.message}\`);
                        }
                    });
                });
            }
        });

    document.querySelector("#paymentMethodForm").style.display = "block";
}

async function processPayment() {

    document.querySelector("#paymentMethodForm").style.display = "none";

    document.querySelector("#result").style.display = "block";
    document.querySelector("#outcome").textContent = \`Please wait while we process your payment!\`;
    
    const response = await fetch("/checkout/process-payment", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ authkey }),
    });

    const status = await response.status;    
    if (status != 200) {
        document.querySelector("#outcome").textContent = \`Payment could not be processed at this time.\`;
        return;
    }

    const result = await response.json();    
    if (result.responseCode == "0") {
        document.querySelector("#outcome").textContent = \`Your payment is successful.  Your receipt number is  \${result.receiptNumber}.\`;
    } else {
        document.querySelector("#outcome").textContent = \`Your payment was declined.  Your receipt number is  \${result.receiptNumber}.\`;
    }       
}            
`,
            copyButton: true
        },

        {
            step: "Step 7 - Add CSS for styling",
            description: <p>In the <code>public</code> folder, create a new file called <code>payment.css</code> and add the code below.
                The styles to look out for are <code>.frame-field</code> which provides styling for the payment detail and a different style is applied when payment details change state such as:
                <ul>
                    <li>in-focus, see: <code>.frame-field.is-focused</code> style</li>
                    <li>invalid, see: <code>.frame-field.is-invalid</code> style</li>
                    <li>valid, see: <code>.frame-field.is-valid</code> style</li>
                </ul>
            </p>,
            DocoUrl: null,
            language: 'CSS',
            code: `
body {
    display: flex;
    justify-content: center;
    align-content: center;
    font-family: sans-serif;
    font-size: 16px;
    padding: 50px
}

.fieldGroup {
    padding: 5px
}

.frame-field {
    border: 1px solid #333333;
    background-color: #fefefe;
    height: 1.4em;
    padding: 0.1em;
    border-radius: 5px;
}

.frame-field.is-focused {
    color: #495057;
    background-color: #fff;
    border-color: #80bdff;
    outline: 0;
    box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

.frame-field.is-invalid {
    border-color: #dc3545;
}

.frame-field.is-valid {
    border-color: #28a745;
}                   
`,
            copyButton: true
        },
       
        {
            step: "Step 8 - Add Client RESTful Library reference to project",
            description: <p>You will need to find <code>BpointQuickStart.ClientLib.csproj</code> in your folder structure on disk.  If you have been following along,
            all you need to do is run the command below from <code>C:\QuickStart\BPoint.QuickStart\</code></p>,
            DocoUrl: null,
            language: 'CMD',
            code: `dotnet add reference ..\\ClientLib\\src\\BpointQuickStart.ClientLib\\BpointQuickStart.ClientLib.csproj`,
            copyButton: true
        },

        {
            step: "Step 9 - Create Checkout Controller",
            description: <p>In the main project folder (<code>C:\QuickStart\BPoint.QuickStart\</code>), create a new folder called <code>Controller</code> and add <code>CheckoutController.cs</code> file.  The controller has two functions which correspond to API calls coming from the JavaScript implementation.
                <ul>
                    <li><code>InitiatePayment()</code> - handles requests to create AuthKey and attach TxnDetailsRequest which contains information about the customer such as CRNs (customer reference numbers),
                        amount, currency, etc.  For demonstration purposes these are hard coded.
                    </li>
                    <li><code>ProcessPayment()</code> - handles requests to process the payment.</li>
                </ul>
                In this step, the Client RESTful Library comes in handy as most of the objects and API calls are already handled for us.
            </p>,
            DocoUrl: null,
            language: 'C#',
            code: `
using Bpoint.QuickStart.Model;
using BpointQuickStart.ClientLib.Api;
using BpointQuickStart.ClientLib.Client;
using BpointQuickStart.ClientLib.Model;
using Microsoft.AspNetCore.Mvc;
using Microsoft.AspNetCore.Http;

namespace Bpoint.QuickStart
{
    [Route("checkout")]
    [ApiController]
    public class CheckoutController : Controller
    {
        // Set UserName, MerchantNumber and Password 
        private static readonly string UserName = "User goes here...";
        private static readonly string MerchantNumber = "Merchant Number goes here...";
        private static readonly string Password = "Password goes here...";

        private static Configuration GetConfiguration()
        {
            return new Configuration
            {
                Password = Password,
                Username = $"{UserName}|{MerchantNumber}"
            };
        }

        [HttpGet("initiate-payment")]
        public ActionResult InitiatePayment()
        {
            TxnAuthkeyApi api = new TxnAuthkeyApi(GetConfiguration());
            CreateAuthkeyResponse createAuthkeyResponse = api.TxnAuthkeyCreateAuthkey();

            if (createAuthkeyResponse == null)
            {
                return new StatusCodeResult(StatusCodes.Status500InternalServerError);
            }

            api.TxnAuthkeyAttachTxnDetails(createAuthkeyResponse.Authkey,
                new TxnDetailsRequest(
                    TxnDetailsRequest.ActionEnum.Payment,
                    TxnDetailsRequest.TypeEnum.Internet, 
                    TxnDetailsRequest.SubTypeEnum.Single, 
                    amount: 10000, 
                    crn1: "Crn1", 
                    currency: "AUD"));

            return new OkObjectResult(createAuthkeyResponse.Authkey);
        }

        [HttpPost("process-payment")]
        public ActionResult ProcessPayment(ProcessPaymentPayload processPaymentPayload)
        {
            TxnAuthkeyApi api = new TxnAuthkeyApi(GetConfiguration());
            ProcessTxnResponse processTxnResponse = api.TxnAuthkeyProcess(processPaymentPayload.AuthKey, new TxnProcessRequest());

            if (processTxnResponse == null)
            {
                return new StatusCodeResult(StatusCodes.Status500InternalServerError);
            }

            return new OkObjectResult(new
            {
                ResponseCode = processTxnResponse.Txn.ResponseCode,
                ReceiptNumber = processTxnResponse.Txn.ReceiptNumber
            });
        }
    }
}             
`,
            copyButton: true
        },

        {
            step: "Step 10 - Add Username, MerchantNumber and Password",
            description: <p>Never do we encourage in production environments to store credentials in code, but for demo purposes you can add your test/UAT Username, 
                Merchant Number and Password into the <code>CheckoutController.cs</code>.
            </p>,
            DocoUrl: null,
            language: 'C#',
            code: `
private static readonly string UserName = "User goes here...";
private static readonly string MerchantNumber = "Merchant Number goes here...";
private static readonly string Password = "Password goes here...";
            `
        },

        {
            step: "Step 11 - Build",
            description: <p>Execute command to Build project from <code>C:\QuickStart\BPoint.QuickStart\</code></p>,
            DocoUrl: null,
            language: 'CMD',
            code: `dotnet build`,
            copyButton: true
        },
        
        
        {
            step: "Step 12 - Start your demo!",
            description: <p>Execute command Run to start your project.</p>,
            DocoUrl: null,
            language: 'CMD',            
            code: `dotnet run`,
            copyButton: true
        },

        
        {
            step: "Step 13 - You are ready!  Execute your first payment.",
            description: <p>Open your browser using the URL below to execute your first payment.</p>,
            DocoUrl: null,
            language: 'CMD',
            code: `http://127.0.0.1:5333/payment.html`,
            copyButton: true
        },
    ]